import { useLoginWithChatGPT } from "@opencoredev/loginwithchatgpt-react";
import { LogOutIcon, ShieldCheckIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Spinner } from "../ui/spinner";
import type { HostedProviderState } from "../../hosted/api";
import { useHostedAuthorizedFetch } from "../../hosted/useHostedApiClient";

const CHATGPT_HANDLER_BASE_PATH = "/api/chatgpt";

/**
 * Login with ChatGPT connection control.
 *
 * Signing in gives this app spending power on the user's ChatGPT plan, so the
 * consent checkbox gates `login()` — the SDK enforces the same rule in its own
 * widget, and a custom UI has to render equivalent consent itself. Models are
 * discovered per account after connecting rather than assumed.
 */
export function HostedChatGptControl({
  backendState,
}: {
  /** Provider row the Worker reported, or null while it is still loading. */
  readonly backendState: HostedProviderState | null;
}) {
  const [consented, setConsented] = useState(false);
  const [models, setModels] = useState<ReadonlyArray<string> | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  // The Worker keeps `/api/chatgpt/*` behind its owner check, so every request
  // the SDK makes needs the Clerk bearer on top of its own session cookie.
  const authorizedFetch = useHostedAuthorizedFetch();

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const chatgpt = useLoginWithChatGPT({
    basePath: CHATGPT_HANDLER_BASE_PATH,
    fetch: authorizedFetch,
  });

  const backendUnavailable = backendState?.status === "unavailable";

  const loadModels = useCallback(async () => {
    try {
      const response = await authorizedFetch(`${CHATGPT_HANDLER_BASE_PATH}/models`, {
        headers: { accept: "application/json" },
      });
      if (!response.ok) {
        throw new Error(
          response.status === 401
            ? "ChatGPT session expired. Connect again."
            : response.status === 503
              ? "Login with ChatGPT is not configured on this deployment."
              : "Could not read the account's models.",
        );
      }
      const body = (await response.json()) as unknown;
      const list = Array.isArray(body)
        ? body
        : Array.isArray((body as { models?: unknown })?.models)
          ? ((body as { models: unknown[] }).models as unknown[])
          : [];
      if (!isMountedRef.current) return;
      setModels(list.filter((entry): entry is string => typeof entry === "string"));
      setModelsError(null);
    } catch (error) {
      if (!isMountedRef.current) return;
      setModels(null);
      setModelsError(error instanceof Error ? error.message : "Could not read models.");
    }
  }, [authorizedFetch]);

  // Availability is per account and plan, so the model list is only meaningful
  // once a session exists. Backend-reported models win when present.
  useEffect(() => {
    if (backendUnavailable || !chatgpt.isAuthenticated) {
      return;
    }
    if (backendState && backendState.models.length > 0) {
      setModels(backendState.models);
      setModelsError(null);
      return;
    }
    void loadModels();
  }, [backendState, backendUnavailable, chatgpt.isAuthenticated, loadModels]);

  if (backendUnavailable) {
    return (
      <ProviderShell
        title="ChatGPT"
        badge={
          <Badge size="sm" variant="outline">
            Unavailable
          </Badge>
        }
        description={
          backendState?.detail ??
          "Login with ChatGPT is not configured on this deployment yet. Use Codex or Claude through a local companion."
        }
      />
    );
  }

  if (chatgpt.status === "loading") {
    return (
      <ProviderShell
        title="ChatGPT"
        badge={
          <Badge size="sm" variant="outline">
            Checking
          </Badge>
        }
        description="Reading the ChatGPT session."
      />
    );
  }

  if (chatgpt.isAuthenticated) {
    const accountLabel = chatgpt.user?.email ?? backendState?.accountLabel ?? null;
    return (
      <ProviderShell
        title="ChatGPT"
        badge={
          <Badge size="sm" variant="success">
            Connected
          </Badge>
        }
        description={
          accountLabel ? `Signed in as ${accountLabel}.` : "Signed in with your ChatGPT account."
        }
        action={
          <Button
            size="xs"
            variant="ghost"
            onClick={() => {
              setModels(null);
              setModelsError(null);
              setConsented(false);
              void chatgpt.logout();
            }}
          >
            <LogOutIcon className="size-3" />
            Disconnect
          </Button>
        }
      >
        {modelsError ? (
          <p className="text-xs text-destructive-foreground" role="alert">
            {modelsError}
          </p>
        ) : models === null ? (
          <p className="text-xs text-muted-foreground/70">Discovering available models…</p>
        ) : models.length === 0 ? (
          <p className="text-xs text-warning-foreground">
            This ChatGPT account exposes no usable models.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1" data-testid="hosted-chatgpt-models">
            {models.map((model) => (
              <Badge key={model} size="sm" variant="outline">
                {model}
              </Badge>
            ))}
          </div>
        )}
      </ProviderShell>
    );
  }

  if (chatgpt.isPending) {
    return (
      <ProviderShell
        title="ChatGPT"
        badge={
          <Badge size="sm" variant="info">
            Waiting
          </Badge>
        }
        description="Enter the code on the OpenAI verification page to finish connecting."
      >
        <div className="flex flex-wrap items-center gap-2">
          {chatgpt.userCode ? (
            <code className="rounded-md border border-border/70 bg-background/70 px-2.5 py-1 font-mono text-sm tracking-[0.2em]">
              {chatgpt.userCode}
            </code>
          ) : null}
          <Button size="xs" variant="outline" onClick={() => void chatgpt.copyCode()}>
            {chatgpt.copied ? "Copied" : "Copy code"}
          </Button>
          <Button size="xs" variant="ghost" onClick={chatgpt.reopen}>
            Reopen verification
          </Button>
        </div>
      </ProviderShell>
    );
  }

  const errorMessage =
    chatgpt.error ??
    (chatgpt.status === "expired" ? "The previous ChatGPT session expired." : null) ??
    backendState?.detail ??
    null;

  return (
    <ProviderShell
      title="ChatGPT"
      badge={
        <Badge size="sm" variant={chatgpt.status === "error" ? "error" : "outline"}>
          {chatgpt.status === "error" ? "Error" : "Disconnected"}
        </Badge>
      }
      description="Run this project's chat on your own ChatGPT plan."
    >
      <label className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
        <Checkbox
          checked={consented}
          className="mt-0.5"
          onCheckedChange={(checked) => setConsented(checked === true)}
        />
        <span>I understand requests run against my ChatGPT plan and consume its usage limits.</span>
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={!consented || chatgpt.isConnecting}
          onClick={() => void chatgpt.login()}
        >
          {chatgpt.isConnecting ? (
            <Spinner className="size-4" />
          ) : (
            <ShieldCheckIcon className="size-4" />
          )}
          {chatgpt.isConnecting ? "Connecting…" : "Login with ChatGPT"}
        </Button>
        {!consented ? (
          <span className="text-xs text-muted-foreground/70">Consent is required to continue.</span>
        ) : null}
      </div>
      {errorMessage ? (
        <p className="text-xs text-destructive-foreground" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </ProviderShell>
  );
}

function ProviderShell({
  title,
  badge,
  description,
  action,
  children,
}: {
  readonly title: string;
  readonly badge: React.ReactNode;
  readonly description: string;
  readonly action?: React.ReactNode;
  readonly children?: React.ReactNode;
}) {
  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-border/70 bg-card/25 p-4"
      data-testid="hosted-chatgpt-control"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-foreground">{title}</h3>
            {badge}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground/80">{description}</p>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </div>
  );
}
